import { useState, useCallback } from 'react';
import { Upload, CheckCircle2, XCircle, FileUp } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { useCampaignStore } from '@/stores/campaignStore';
import { useImportProgressStore } from '@/stores/importProgressStore';
import { useFileUpload } from '@/hooks/useFileUpload';
import { useRequestUpload } from '@/hooks/queries';
import type { ImportJobHistoryDto } from '@signalvote/shared';
import VoterStagingImport from '@/components/voters/VoterStagingImport';
import { VoterFileRequestDialog } from '@/components/onboarding';
import { ImportHistoryList } from './ImportHistoryList';
import { ImportErrorReview } from './ImportErrorReview';

interface MappingState {
  columns: string[];
  sampleData: Record<string, string>[];
  columnMapping: Record<string, string>;
  selectedTags: number[];
  delimiter: string;
  fileKey: string | null;
  fileName: string | null;
}

const INITIAL_MAPPING: MappingState = {
  columns: [],
  sampleData: [],
  columnMapping: {},
  selectedTags: [],
  delimiter: ',',
  fileKey: null,
  fileName: null,
};

export function VoterDataImportCard() {
  const selectedCampaign = useCampaignStore((s) => s.selectedCampaign);
  const canManageTeam = useCampaignStore((s) => s.canManage('TeamManagement'));
  const [open, setOpen] = useState(false);
  const [requestOpen, setRequestOpen] = useState(false);
  const [errorReviewJob, setErrorReviewJob] = useState<ImportJobHistoryDto | null>(null);

  // Read import progress from Zustand store (banner handles display)
  const progress = useImportProgressStore((s) => s.progress);
  const bumpSse = useImportProgressStore((s) => s.bumpSse);
  const isImporting = progress && (progress.status === 'Running' || progress.status === 'Pending');

  // Upload state lives here (survives dialog close)
  const uploadHook = useFileUpload();

  // Mapping state lives here (survives dialog close)
  const [mappingState, setMappingState] = useState<MappingState>(INITIAL_MAPPING);

  const handleImportStarted = useCallback(() => {
    // Import kicked off — close dialog, reconnect SSE to pick up new job
    setOpen(false);
    bumpSse();
  }, [bumpSse]);

  const handleReset = useCallback(() => {
    uploadHook.reset();
    setMappingState(INITIAL_MAPPING);
  }, [uploadHook]);

  const { mutateAsync: requestUpload, isPending: requestUploadPending } = useRequestUpload();

  const handleRetry = useCallback(async () => {
    // fileRef.current is stored inside uploadHook — retrieve the file name from
    // the upload state so we can request a fresh presigned URL for the same file.
    const fileName = uploadHook.fileName;
    if (!fileName) return;

    try {
      const { uploadUrl, fileKey, usesPresignedUrls } = await requestUpload(fileName);
      uploadHook.retry(uploadUrl, fileKey, usesPresignedUrls);
    } catch {
      // requestUpload failure is surfaced via its own error state; nothing extra needed here.
    }
  }, [uploadHook, requestUpload]);

  if (!selectedCampaign || !canManageTeam) return null;

  // Show upload banner when dialog is closed and upload is happening
  const showUploadBanner = !open && (uploadHook.uploading || uploadHook.complete || uploadHook.error);

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Upload className="h-5 w-5" />
            Voter Data Import
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {/* Upload banner (shown when dialog closed but upload in progress) */}
          {showUploadBanner && (
            <div className="flex items-center gap-2 p-3 rounded-md bg-muted">
              {uploadHook.uploading && !uploadHook.stalled && (
                <>
                  <FileUp className="h-4 w-4 animate-pulse" />
                  <span className="text-sm flex-1">
                    Uploading {uploadHook.fileName} — {uploadHook.progress}%
                  </span>
                  <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
                    Open
                  </Button>
                </>
              )}
              {uploadHook.uploading && uploadHook.stalled && (
                <>
                  <XCircle className="h-4 w-4 text-warning" />
                  <span className="text-sm flex-1 text-warning">Upload appears stalled</span>
                  <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
                    Open
                  </Button>
                </>
              )}
              {uploadHook.error && (
                <>
                  <XCircle className="h-4 w-4 text-destructive" />
                  <span className="text-sm flex-1 text-destructive">Upload failed</span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleRetry}
                    disabled={requestUploadPending}
                  >
                    {requestUploadPending ? 'Retrying…' : 'Retry'}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={handleReset}>
                    Dismiss
                  </Button>
                </>
              )}
              {uploadHook.complete && !isImporting && (
                <>
                  <CheckCircle2 className="h-4 w-4 text-success" />
                  <span className="text-sm flex-1">Upload complete — ready to map columns</span>
                  <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
                    Continue Mapping
                  </Button>
                </>
              )}
            </div>
          )}

          {!showUploadBanner && (
            <>
              <p className="text-sm text-muted-foreground">
                Import voter data from a CSV file. The import wizard will guide you through
                uploading your file, mapping columns to voter fields, and importing records
                with duplicate detection.
              </p>
              <Button size="sm" onClick={() => setOpen(true)} disabled={!!isImporting}>
                Import Voter Data
              </Button>
              <p className="text-sm text-muted-foreground">
                Don&rsquo;t have a voter file yet?{' '}
                <button
                  type="button"
                  onClick={() => setRequestOpen(true)}
                  className="font-medium text-primary underline-offset-4 hover:underline"
                >
                  Request one
                </button>
              </p>
            </>
          )}
        </CardContent>
      </Card>

      <VoterFileRequestDialog open={requestOpen} onOpenChange={setRequestOpen} />

      {open && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Import Voter Data</DialogTitle>
              <DialogDescription>
                Upload a CSV file and map its columns to voter fields.
              </DialogDescription>
            </DialogHeader>
            <VoterStagingImport
              uploadState={uploadHook}
              mappingState={mappingState}
              onMappingChange={setMappingState}
              onImportStarted={handleImportStarted}
              onReset={handleReset}
            />
          </DialogContent>
        </Dialog>
      )}

      {/* Import History */}
      <ImportHistoryList onReviewErrors={(job) => setErrorReviewJob(job)} />

      {/* Error Review Dialog */}
      {errorReviewJob && (
        <ImportErrorReview
          job={errorReviewJob}
          open={!!errorReviewJob}
          onClose={() => setErrorReviewJob(null)}
        />
      )}
    </>
  );
}
